import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useMoodRecommendations } from './hooks/useMoodRecommendations';
import { useLocation, useNavigate } from 'react-router-dom';
import Navbar from '../Navbar';
import BookImage from '../BookImage';
import LoginModal from '../Auth/LoginModal';
import BookModal from '../BookModal';
import SubscriptionModal from '../Auth/SubscriptionModal';
import { useAuth } from '../Auth/AuthContext';
import { useSubscription } from '../Auth/SubscriptionContext';
import { API_BASE_URL } from '../../config/api';

// Add BatchSeparator component
const BatchSeparator = () => (
  <div className="my-8 flex items-center gap-4">
    <div className="h-px flex-1 bg-purple-100/50"></div>
    <span className="text-sm text-gray-500 font-space">More recommendations</span>
    <div className="h-px flex-1 bg-purple-100/50"></div>
  </div>
);

// BookCard moved outside and memoized
const BookCard = React.memo(function BookCard({ book, isBookInShelf, bookShelfStatus, onAddToShelf, onBookClick }) {
  const [addToShelfStatus, setAddToShelfStatus] = useState(null);
  const bookId = book.id || `book_${book.title.toLowerCase().replace(/[^a-z0-9]/g, '_')}`;

  // Animate add to shelf
  useEffect(() => {
    if (bookShelfStatus === 'success' || bookShelfStatus === 'error') {
      setAddToShelfStatus(bookShelfStatus);
      const timeout = setTimeout(() => setAddToShelfStatus(null), 2000);
      return () => clearTimeout(timeout);
    }
  }, [bookShelfStatus]);

  return (
    <div 
      className="bg-white/90 backdrop-blur-sm rounded-2xl overflow-hidden border border-purple-100/50 hover:border-purple-200/80 transition-all duration-300 shadow-sm hover:shadow-md cursor-pointer"
      onClick={() => onBookClick(book)}
    >
      <div className="flex gap-4 p-3">
        {/* Book thumbnail with enhanced shadow and hover effect */}
        <div className="relative w-[100px] h-[140px] flex-shrink-0 transform transition-transform duration-300 hover:scale-[1.02]">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-100/20 to-blue-100/20 rounded-xl" />
          <BookImage
            src={book.thumbnail}
            alt={book.title}
            className="w-full h-full object-cover rounded-xl shadow-md relative z-10"
          />
          {/* Subtle glow effect */}
          <div className="absolute -inset-1 bg-gradient-to-br from-purple-500/5 to-blue-500/5 rounded-xl blur-md z-0" />
        </div>
        <div className="flex-1 min-w-0 py-1">
          <div className="h-full flex flex-col">
            <div>
              <h3 className="text-gray-900 text-lg font-medium leading-tight font-fraunces line-clamp-2 tracking-tight">
                {book.title}
              </h3>
              <p className="text-gray-500 text-sm font-space mt-1 tracking-tight">by {book.author}</p>
            </div>
            <p className="text-transparent bg-clip-text bg-gradient-to-r from-gray-600 to-gray-500 text-sm font-space line-clamp-2 mt-2 flex-grow">
              {book.explanation}
            </p>
            <div className="mt-2 flex items-center justify-between gap-3">
              <div className="flex gap-2">
                {book.genres?.slice(0, 1).map(genre => (
                  <span 
                    key={genre} 
                    className="px-2.5 py-0.5 bg-gradient-to-r from-purple-50/80 to-blue-50/80 text-purple-700 text-xs font-medium rounded-full border border-purple-100/50 font-space shadow-sm backdrop-blur-sm"
                  >
                    {genre}
                  </span>
                ))}
              </div>
              <div className="flex items-center gap-2">
                {book.amazonLink && (
                  <a
                    href={book.amazonLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                    className="hidden sm:flex items-center px-3 py-1 text-purple-600 text-sm font-medium hover:text-purple-800 transition-colors font-space"
                  >
                    Buy now
                  </a>
                )}
                <button 
                  onClick={e => {
                    e.stopPropagation();
                    if (!isBookInShelf) onAddToShelf(book);
                  }}
                  disabled={isBookInShelf}
                  className={`w-9 h-9 flex items-center justify-center rounded-full transition-all duration-300 group relative shadow-sm hover:shadow-md ${
                    isBookInShelf
                      ? 'bg-gray-100 text-gray-400'
                      : addToShelfStatus === 'success'
                      ? 'bg-gray-100 text-gray-400 animate-addToShelf'
                      : addToShelfStatus === 'error'
                      ? 'bg-red-500 text-white'
                      : 'bg-gradient-to-r from-purple-500 to-blue-500 text-white hover:from-purple-600 hover:to-blue-600'
                  }`}
                  aria-label={isBookInShelf ? 'Already in shelf' : 'Add to shelf'}
                >
                  {/* Sparkle animation container */}
                  {addToShelfStatus === 'success' && (
                    <div className="absolute inset-0 overflow-visible pointer-events-none">
                      {[...Array(8)].map((_, i) => (
                        <div
                          key={i}
                          className="absolute left-1/2 top-1/2 w-1.5 h-1.5 rounded-full"
                          style={{
                            background: i % 2 ? 'rgb(147, 51, 234)' : 'rgb(59, 130, 246)',
                            animation: 'sparkleOut 0.6s ease-out forwards',
                            animationDelay: `${i * 0.05}s`,
                            transform: `translate(-50%, -50%) rotate(${i * 45}deg)`,
                            '--angle': `${i * 45}deg`,
                          }}
                        >
                          <div
                            className="absolute inset-0 rounded-full"
                            style={{
                              animation: 'sparklePulse 0.6s ease-out forwards',
                              animationDelay: `${i * 0.05}s`,
                              background: 'inherit',
                              boxShadow: '0 0 4px currentColor',
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/0 hover:bg-black/10 rounded-full transition-colors duration-300" />
                  <svg 
                    className={`w-[18px] h-[18px] transform transition-all duration-300 ${
                      addToShelfStatus === 'success'
                        ? 'scale-150 animate-successPop'
                        : 'group-hover:scale-110'
                    }`}
                    viewBox="0 0 24 24" 
                    fill="currentColor"
                    stroke="none"
                  >
                    <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

// Helper to compare book IDs
function areBookIdsEqual(a, b) {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i]?.id !== b[i]?.id) return false;
  }
  return true;
}

function RecommendationsView({ 
  recommendations: initialRecommendations, 
  mode, 
  selectedMood, 
  selectedBooks, 
  onNewSearch,
  originalSimilarInput = []
}) {
  // State for similar books mode
  const [showPaywall, setShowPaywall] = useState(false);
  const [apiUsage, setApiUsage] = useState(null);
  // Add state to track shelved books for similar books flow
  const [shelvedBooks, setShelvedBooks] = useState(new Set());
  const [shelvedBooksStatus, setShelvedBooksStatus] = useState({});

  // State for mood-based recommendations
  const [state, setState] = useState(() => {
    const initialBookCount = initialRecommendations?.length || 0;
    
    return {
      requestCount: 0,
      initialBooks: initialRecommendations,
      additionalBooks: [],
      totalBooksShown: initialBookCount,
      hasShownPaywall: false // Track if paywall was shown for this mood
    };
  });

  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const { getRecommendations, getMoreSimilarBooks } = useMoodRecommendations();
  const additionalBooksRef = useRef(null);

  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showNewSimilarSearchModal, setShowNewSimilarSearchModal] = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);
  const [showBookModal, setShowBookModal] = useState(false);
  const { isAuthenticated, refreshAuth } = useAuth();
  const { isPro } = useSubscription();
  
  // Add state for scroll position
  const [scrollY, setScrollY] = useState(0);
  
  // Reference to store the pending book to be added after login
  const pendingBookRef = useRef(null);
  const location = useLocation();

  // State for original input for similar books
  const [similarInput, setSimilarInput] = useState(originalSimilarInput);
  const [limitError, setLimitError] = useState(null);

  // Memoize book batches for rendering
  const moodBatches = useMemo(() => {
    if (!state.additionalBooks.length) return [];
    return Array.from({ length: Math.ceil(state.additionalBooks.length / 4) }).map((_, batchIndex) => {
      return state.additionalBooks.slice(batchIndex * 4, (batchIndex + 1) * 4);
    });
  }, [state.additionalBooks]);

  // Global function to handle adding a book to shelf
  const handleAddToShelf = useCallback(async (book, skipAuthCheck = false) => {
    console.log('handleAddToShelf called', { skipAuthCheck, isAuthenticated });
    
    if (!skipAuthCheck && !isAuthenticated) {
      console.log('User not authenticated, showing login modal');
      pendingBookRef.current = book;
      setShowSubscriptionModal(true);
      return;
    }

    try {
      const bookId = book.id || `book_${book.title.toLowerCase().replace(/[^a-z0-9]/g, '_')}`;
      console.log('Adding book to shelf:', { bookId, title: book.title });
      
      const response = await fetch(`${API_BASE_URL}/api/shelf/books`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          bookId,
          title: book.title,
          author: book.author,
          thumbnail: book.thumbnail,
          description: book.explanation || book.description, // Use explanation or fallback to description
          rating: book.rating,
          year: book.year,
          category: book.category
        })
      });

      if (!response.ok) {
        if (response.status === 401) {
          // Session expired, refresh auth state and show login modal
          await refreshAuth();
          pendingBookRef.current = book;
          setShowSubscriptionModal(true);
          return;
        }
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add book to shelf');
      }

      console.log('Successfully added book to shelf');
      
      // Update the shelved books state for both modes
      setShelvedBooks(prev => {
        const newSet = new Set(prev);
        newSet.add(bookId);
        return newSet;
      });
      
      setShelvedBooksStatus(prev => ({
        ...prev,
        [bookId]: 'success'
      }));
      
      // Reset status after animation
      setTimeout(() => {
        setShelvedBooksStatus(prev => ({
          ...prev,
          [bookId]: null
        }));
      }, 2000);
      
      return 'success';
    } catch (error) {
      console.error('Error adding book to shelf:', error);
      
      // Update error status for both modes
      const bookId = book.id || `book_${book.title.toLowerCase().replace(/[^a-z0-9]/g, '_')}`;
      setShelvedBooksStatus(prev => ({
        ...prev,
        [bookId]: 'error'
      }));
      
      // Reset status after animation
      setTimeout(() => {
        setShelvedBooksStatus(prev => ({
          ...prev,
          [bookId]: null
        }));
      }, 2000);
      
      return 'error';
    }
  }, [isAuthenticated, refreshAuth]);

  // Memoize handlers
  const handleBookClick = useCallback((book) => {
    setSelectedBook(book);
    setShowBookModal(true);
  }, []);

  const handleAddToShelfMemo = useCallback(
    async (book) => {
      return handleAddToShelf(book);
    },
    [handleAddToShelf]
  );

  function areArraysEqual(a, b) {
    if (a === b) return true;
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (a[i]?.id !== b[i]?.id) return false;
    }
    return true;
  }

  // Replace areArraysEqual with areBookIdsEqual
  const prevInitRef = useRef();
  const prevModeRef = useRef();
  useEffect(() => {
    if (
      !areBookIdsEqual(prevInitRef.current, initialRecommendations) ||
      prevModeRef.current !== mode
    ) {
      setState({
        requestCount: 0,
        initialBooks: initialRecommendations,
        additionalBooks: [],
        totalBooksShown: initialRecommendations?.length || 0,
        hasShownPaywall: false
      });
      prevInitRef.current = initialRecommendations;
      prevModeRef.current = mode;
    }
  }, [initialRecommendations, mode]);

  // Add scroll event listener
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Removed problematic useEffect that was causing infinite loop
  // similarInput is already initialized with originalSimilarInput

  // Add a function to fetch API usage - for all users
  const fetchApiUsage = useCallback(async () => {
    if (mode !== 'similar') return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/usage/similar-books`, {
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setApiUsage(data);
      }
    } catch (error) {
      console.error('Error fetching API usage:', error);
    }
  }, [mode]);

  // Add a function to fetch mood usage - for all users
  const fetchMoodUsage = useCallback(async () => {
    if (mode !== 'mood' || !selectedMood) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/usage/mood-recommendations?mood=${selectedMood.mood}`, {
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('🔍 Backend mood usage:', data);
        return data;
      }
    } catch (error) {
      console.error('Error fetching mood usage:', error);
    }
    return null;
  }, [mode, selectedMood]);

  // Call this function when the component mounts
  useEffect(() => {
    fetchApiUsage();
  }, [mode]);

  // Call mood usage fetch when mood changes
  useEffect(() => {
    if (mode === 'mood' && selectedMood) {
      fetchMoodUsage();
    }
  }, [mode, selectedMood]);

  // Check for limitReached in location state
  useEffect(() => {
    const { limitReached } = location.state || {};
    if (limitReached && mode === 'similar') {
      if (!isAuthenticated) {
        setShowSubscriptionModal(true);
      } else {
        setShowSubscriptionModal(true);
      }
    }
  }, [location.state, mode, isAuthenticated]);

  // Add effect to scroll to new books when loaded
  useEffect(() => {
    if (additionalBooksRef.current && !isLoadingMore) {
      additionalBooksRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [state.additionalBooks.length, isLoadingMore]);

  // Load state from session storage on mount and when mood changes
  useEffect(() => {
    if (mode === 'mood' && selectedMood) {
      // Check backend usage to see if we should show paywall
      const checkBackendUsage = async () => {
        try {
          const moodUsage = await fetchMoodUsage();
          if (moodUsage && moodUsage.total && moodUsage.total.remaining <= 0) {
            console.log('🚨 Backend shows limit reached, showing appropriate modal');
            if (!isAuthenticated) {
              setShowSubscriptionModal(true);
            } else {
              setShowSubscriptionModal(true);
            }
          }
        } catch (error) {
          console.error('Error checking backend usage:', error);
        }
      };
      checkBackendUsage();
    }
  }, [mode, selectedMood, isAuthenticated]);

  // Persist state to session storage
  useEffect(() => {
    // No longer persisting to session storage - relying on backend tracking
  }, [mode, selectedMood]);

  // Check for pending book after login
  useEffect(() => {
    if (isAuthenticated && pendingBookRef.current) {
      console.log('Found pending book after login:', pendingBookRef.current.title);
      handleAddToShelfMemo(pendingBookRef.current);
      pendingBookRef.current = null;
    }
  }, [isAuthenticated, handleAddToShelfMemo]);

  // Check global total on mount to show paywall if limit is already reached
  useEffect(() => {
    // No longer checking session storage - relying on backend tracking
  }, [mode, selectedMood]);

  // Reference to always hold the original input for similar books
  const originalSimilarInputRef = useRef(originalSimilarInput);
  useEffect(() => {
    if (originalSimilarInput && originalSimilarInput.length > 0) {
      originalSimilarInputRef.current = originalSimilarInput;
    }
  }, [originalSimilarInput]);

  const handleFindMore = async () => {
    if (mode === 'similar') {
      if (!isAuthenticated) {
        setShowSubscriptionModal(true);
        return;
      }
      setIsLoadingMore(true);
      try {
        // Gather all books currently shown (initial + additional)
        const allShownBooks = [...state.initialBooks, ...state.additionalBooks];
        // Defensive: always use the original input, fallback to selectedBooks or state.initialBooks if needed
        let originalInput = originalSimilarInputRef.current;
        if (!originalInput || originalInput.length === 0) {
          originalInput = selectedBooks && selectedBooks.length > 0 ? selectedBooks : state.initialBooks;
        }
        // Call the new endpoint for more similar books
        const newRecommendations = await getMoreSimilarBooks(originalInput, allShownBooks);
        if (!newRecommendations || !Array.isArray(newRecommendations)) {
          throw new Error('Invalid response format');
        }
        const currentIds = new Set(allShownBooks.map(book => book.id));
        const additionalUniqueBooks = newRecommendations.filter(book => !currentIds.has(book.id));
        if (additionalUniqueBooks.length > 0) {
          setState(prev => {
            const newTotalBooks = prev.totalBooksShown + additionalUniqueBooks.length;
            return {
              ...prev,
              additionalBooks: [...prev.additionalBooks, ...additionalUniqueBooks],
              totalBooksShown: newTotalBooks,
              hasShownPaywall: false
            };
          });
        }
      } catch (error) {
        console.error('Error fetching more similar recommendations:', error);
        // Show modal and error message if limit reached
        if (error?.limitReached || (error?.message && error.message.toLowerCase().includes('limit'))) {
          setLimitError(error.message || 'You have reached your daily limit.');
          setShowSubscriptionModal(true);
        } else if (!isAuthenticated) {
          setShowSubscriptionModal(true);
        } else if (!isPro) {
          setShowSubscriptionModal(true);
        }
      } finally {
        setIsLoadingMore(false);
      }
      return;
    }

    // Handle mood-based recommendations
    const moodUsage = await fetchMoodUsage();
    if (moodUsage) {
      if (!isPro && moodUsage.total && moodUsage.total.remaining <= 0) {
        if (!isAuthenticated) {
          setShowSubscriptionModal(true);
        } else {
          setShowSubscriptionModal(true);
        }
        return;
      }
    }
    setIsLoadingMore(true);
    try {
      const frontendLimit = isAuthenticated ? 16 : 8;
      const remainingSlots = frontendLimit - state.totalBooksShown;
      const requestCount = Math.min(4, remainingSlots);
      let uniqueNewBooks = [];
      let attempts = 0;
      const maxAttempts = 3;
      while (uniqueNewBooks.length < requestCount && attempts < maxAttempts) {
        const allExistingBooks = [...state.initialBooks, ...state.additionalBooks, ...uniqueNewBooks];
        // Use the new getRecommendations signature for mood
        const newRecommendations = await getRecommendations('mood', selectedMood.mood, requestCount, allExistingBooks);
        if (!newRecommendations || !Array.isArray(newRecommendations)) {
          throw new Error('Invalid response format');
        }
        const currentIds = new Set([
          ...state.initialBooks, 
          ...state.additionalBooks,
          ...uniqueNewBooks
        ].map(book => book.id));
        const additionalUniqueBooks = newRecommendations.filter(book => !currentIds.has(book.id));
        uniqueNewBooks = [...uniqueNewBooks, ...additionalUniqueBooks].slice(0, requestCount);
        attempts++;
      }
      if (uniqueNewBooks.length > 0) {
        setState(prev => {
          const newTotalBooks = prev.totalBooksShown + uniqueNewBooks.length;
          if (!isPro && newTotalBooks >= frontendLimit) {
            setTimeout(() => {
              if (!isAuthenticated) {
                setShowSubscriptionModal(true);
              } else {
                setShowSubscriptionModal(true);
              }
            }, 500);
          }
          return {
            ...prev,
            additionalBooks: [...prev.additionalBooks, ...uniqueNewBooks],
            totalBooksShown: newTotalBooks,
            hasShownPaywall: false
          };
        });
      }
    } catch (error) {
      console.error('Error fetching more recommendations:', error);
      if (error.message && error.message.includes('limit reached')) {
        if (!isAuthenticated) {
          setShowSubscriptionModal(true);
        } else {
          setShowSubscriptionModal(true);
        }
      }
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Handle subscription modal dismissal
  const handleSubscriptionModalDismiss = () => {
    setShowSubscriptionModal(false);
    setLimitError(null);
    // No longer persisting to session storage - relying on backend tracking
  };

  // Handle book modal
  const handleCloseBookModal = () => {
    setShowBookModal(false);
    setSelectedBook(null);
  };

  // Fetch shelved books when component mounts or auth state changes
  useEffect(() => {
    const fetchShelvedBooks = async () => {
      if (!isAuthenticated) {
        setShelvedBooks(new Set());
        return;
      }
      
      try {
        const response = await fetch(`${API_BASE_URL}/api/shelf/books`, {
          credentials: 'include',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          }
        });
        
        if (!response.ok) {
          if (response.status === 401) {
            await refreshAuth();
            return;
          }
          throw new Error('Failed to fetch shelf');
        }
        
        const books = await response.json();
        const bookIds = new Set(books.map(b => b.bookId));
        setShelvedBooks(bookIds);
        console.log('Fetched shelved books:', bookIds.size, 'books');
      } catch (error) {
        console.error('Error fetching shelved books:', error);
        setShelvedBooks(new Set());
      }
    };

    fetchShelvedBooks();
  }, [isAuthenticated]);

  return (
    <div className="relative min-h-screen">
      {/* Purple gradient background */}
      <div className="fixed inset-0 bg-gradient-to-br from-purple-100/80 via-white to-blue-100/80 -z-10" />

      <Navbar showBackButton={true} onBackClick={onNewSearch} />

      {/* Main Content Layout */}
      <div className="pt-16 flex min-h-[calc(100vh-4rem)]">
        <div className="w-full max-w-7xl mx-auto flex flex-col sm:flex-row relative">
          {/* Desktop: Fixed Left Section - 30% */}
          <div className="hidden sm:block w-[30%] fixed max-w-[360px]">
            <div className="pt-12 px-8 space-y-6">
              <div className="space-y-4">
                <div>
                  <h2 className="text-3xl font-medium text-gray-900 leading-tight font-fraunces">
                    {mode === 'mood' 
                      ? `Reading recommendations for ${selectedMood.text.toLowerCase()}`
                      : 'Similar books you might enjoy'
                    }
                  </h2>
                  {mode === 'similar' && selectedBooks.length > 0 && (
                    <div className="mt-2 text-gray-500 text-base font-space">
                      Based on {selectedBooks.map(book => book.title).join(' and ')}
                    </div>
                  )}
                </div>
                {mode === 'similar' && initialRecommendations[0]?.analysis && (
                  <p className="text-gray-600 text-base font-space leading-relaxed">
                    {initialRecommendations[0].analysis}
                  </p>
                )}
              </div>
              <button
                onClick={handleFindMore}
                // Remove frontend-side disabling for mood-based recommendations
                disabled={mode === 'mood' && isLoadingMore}
                className="w-full px-4 py-2.5 bg-gradient-to-r from-purple-600 to-blue-500 text-white text-sm font-medium rounded-full hover:from-purple-700 hover:to-blue-600 transition-all shadow-sm font-space disabled:opacity-50"
              >
                {mode === 'mood' 
                  ? 'See more recommendations'
                  : 'See more recommendations'
                }
              </button>
            </div>
          </div>

          {/* Mobile: Sticky Title Section */}
          <div className="sm:hidden sticky top-16 z-40">
            <div className={`transition-all duration-300 ease-in-out bg-white/80 backdrop-blur-md border-b border-purple-100/30 ${
              scrollY > 50 ? 'py-2.5 shadow-sm' : 'py-6'
            }`}>
              <div className={`px-4 ${scrollY > 50 ? 'space-y-1' : 'space-y-3'}`}>
                <div className="flex items-start">
                  {scrollY > 50 && (
                    <button 
                      onClick={onNewSearch}
                      className="mr-2 p-1 text-purple-600 hover:text-purple-800 transition-colors mt-0.5 flex-shrink-0"
                      aria-label="Back to search"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                      </svg>
                    </button>
                  )}
                  <div className="transition-all duration-300 ease-in-out">
                    <h2 className={`transition-all duration-300 ease-in-out font-fraunces leading-tight ${
                      scrollY > 50 ? 'text-xl' : 'text-2xl'
                    }`}>
                      {mode === 'mood' 
                        ? `Reading recommendations for ${selectedMood.text.toLowerCase()}`
                        : 'Similar books you might enjoy'
                      }
                    </h2>
                  </div>
                </div>
                {mode === 'similar' && selectedBooks.length > 0 && (
                  <div className={`transition-all duration-300 ease-in-out text-gray-500 font-space ${
                    scrollY > 50 ? 'text-xs mt-0.5 line-clamp-1' : 'text-sm mt-2'
                  }`}>
                    Based on {selectedBooks.map(book => book.title).join(' and ')}
                  </div>
                )}
                {mode === 'similar' && initialRecommendations[0]?.analysis && (
                  <p className={`transition-all duration-300 ease-in-out text-gray-600 font-space leading-relaxed ${
                    scrollY > 50 ? 'text-xs max-h-0 opacity-0 overflow-hidden' : 'text-sm max-h-40 opacity-100'
                  }`}>
                    {initialRecommendations[0].analysis}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="w-full sm:w-[70%] sm:ml-[30%]">
            <div className="max-w-3xl py-4 sm:py-12 px-4 sm:px-8">
              {/* Book Cards */}
              <div className="space-y-4">
                {mode === 'similar' ? (
                  // Similar books mode - show all books at once, including additional batches
                  <>
                    <div className="space-y-4">
                      {initialRecommendations && initialRecommendations.length > 0 && initialRecommendations.map((book) => (
                        <BookCard 
                          key={book.id || book.title} 
                          book={book} 
                          isBookInShelf={shelvedBooks.has(book.id || `book_${book.title.toLowerCase().replace(/[^a-z0-9]/g, '_')}`)} 
                          bookShelfStatus={shelvedBooksStatus[book.id || `book_${book.title.toLowerCase().replace(/[^a-z0-9]/g, '_')}`]} 
                          onAddToShelf={handleAddToShelfMemo} 
                          onBookClick={handleBookClick} 
                        />
                      ))}
                    </div>
                    {state.additionalBooks.length > 0 && (
                      <>
                        <BatchSeparator />
                        <div className="space-y-4">
                          {state.additionalBooks.map((book) => (
                            <BookCard 
                              key={book.id || book.title} 
                              book={book} 
                              isBookInShelf={shelvedBooks.has(book.id || `book_${book.title.toLowerCase().replace(/[^a-z0-9]/g, '_')}`)} 
                              bookShelfStatus={shelvedBooksStatus[book.id || `book_${book.title.toLowerCase().replace(/[^a-z0-9]/g, '_')}`]} 
                              onAddToShelf={handleAddToShelfMemo} 
                              onBookClick={handleBookClick} 
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  // Mood-based mode - show initial books and additional batches
                  <>
                    <div className="space-y-4">
                      {state.initialBooks.map((book) => (
                        <BookCard 
                          key={book.id || book.title} 
                          book={book} 
                          isBookInShelf={shelvedBooks.has(book.id || `book_${book.title.toLowerCase().replace(/[^a-z0-9]/g, '_')}`)} 
                          bookShelfStatus={shelvedBooksStatus[book.id || `book_${book.title.toLowerCase().replace(/[^a-z0-9]/g, '_')}`]} 
                          onAddToShelf={handleAddToShelfMemo} 
                          onBookClick={handleBookClick} 
                        />
                      ))}
                    </div>

                    {state.additionalBooks.length > 0 && (
                      <>
                        {moodBatches.map((batch, batchIndex) => {
                          const isLatestBatch = batchIndex === moodBatches.length - 1;
                          return (
                            <div 
                              key={batchIndex}
                              ref={isLatestBatch ? additionalBooksRef : undefined}
                            >
                              <BatchSeparator />
                              <div className="space-y-4">
                                {batch.map((book) => (
                                  <BookCard 
                                    key={book.id || book.title} 
                                    book={book} 
                                    isBookInShelf={shelvedBooks.has(book.id || `book_${book.title.toLowerCase().replace(/[^a-z0-9]/g, '_')}`)} 
                                    bookShelfStatus={shelvedBooksStatus[book.id || `book_${book.title.toLowerCase().replace(/[^a-z0-9]/g, '_')}`]} 
                                    onAddToShelf={handleAddToShelfMemo} 
                                    onBookClick={handleBookClick} 
                                  />
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </>
                    )}
                  </>
                )}

                {/* Mobile: See More Button */}
                <div className="sm:hidden pt-4 pb-8">
                  <button
                    onClick={handleFindMore}
                    // Remove frontend-side disabling for mood-based recommendations
                    disabled={mode === 'mood' && isLoadingMore}
                    className="w-full px-4 py-2.5 bg-gradient-to-r from-purple-600 to-blue-500 text-white text-sm font-medium rounded-full hover:from-purple-700 hover:to-blue-600 transition-all shadow-sm font-space disabled:opacity-50"
                  >
                    {mode === 'mood'
                      ? 'See more recommendations'
                      : 'See more recommendations'
                    }
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Loading More Modal - For both mood-based and similar recommendations */}
      {(isLoadingMore && (mode === 'mood' || mode === 'similar')) && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white/80 backdrop-blur rounded-2xl p-6 max-w-sm w-full mx-4 shadow-xl border border-purple-100/30">
            <div className="flex items-center gap-4">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-purple-600 border-t-transparent"></div>
              <p className="text-gray-900 font-medium font-space">
                Loading more recommendations...
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Daily Limit Modal */}
      {limitError && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white/90 rounded-2xl p-8 max-w-md w-full mx-4 shadow-xl border border-purple-100/30 text-center space-y-6">
            <h3 className="text-2xl font-medium text-gray-900 font-fraunces">
              Daily Limit Reached
            </h3>
            <p className="text-gray-600 font-space">
              You have reached your daily limit of 15 similar book recommendations for PRO users. Your limit will refresh tomorrow.
            </p>
            <button
              onClick={handleSubscriptionModalDismiss}
              className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl font-space"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Subscription Modal */}
      <SubscriptionModal
        isOpen={showSubscriptionModal && !limitError}
        onClose={handleSubscriptionModalDismiss}
        mode="upgrade"
      />

      {/* Book Details Modal */}
      <BookModal
        book={selectedBook}
        isOpen={showBookModal}
        onClose={handleCloseBookModal}
      />

      {/* Login Modal - Now only at the root level */}
      <LoginModal 
        isOpen={showLoginModal} 
        onClose={() => setShowLoginModal(false)} 
      />

      {/* New Similar Books Search Modal */}
      {showNewSimilarSearchModal && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white/80 backdrop-blur rounded-2xl p-6 max-w-md w-full mx-4 shadow-xl border border-purple-100/30">
            <div className="text-center space-y-4">
              <h3 className="text-xl font-fraunces text-gray-900">
                Start a New Similar Books Search
              </h3>
              <p className="text-gray-600 font-space">
                You can make another similar books request. This will start a fresh search with your selected books.
              </p>
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowNewSimilarSearchModal(false)}
                  className="flex-1 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-space"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowNewSimilarSearchModal(false);
                    if (onNewSearch) {
                      onNewSearch();
                    }
                  }}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-500 text-white rounded-lg hover:from-purple-700 hover:to-blue-600 transition-all font-space"
                >
                  Start New Search
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx global>{`
        @keyframes sparkleOut {
          0% {
            transform: translate(-50%, -50%) rotate(var(--angle)) translateY(0);
            opacity: 1;
          }
          100% {
            transform: translate(-50%, -50%) rotate(var(--angle)) translateY(-40px);
            opacity: 0;
          }
        }
        
        @keyframes sparklePulse {
          0% {
            transform: scale(0.5);
            opacity: 1;
          }
          50% {
            transform: scale(1.5);
            opacity: 0.8;
          }
          100% {
            transform: scale(0);
            opacity: 0;
          }
        }

        @keyframes addToShelf {
          0% { transform: scale(1); }
          50% { transform: scale(1.3); }
          100% { transform: scale(1); background-color: rgb(243 244 246); }
        }
        
        @keyframes successPop {
          0% { transform: scale(1); }
          50% { transform: scale(1.5) rotate(10deg); }
          100% { transform: scale(1); }
        }

        .animate-addToShelf {
          animation: addToShelf 0.5s ease-out forwards;
        }
        
        .animate-successPop {
          animation: successPop 0.5s ease-out forwards;
        }
      `}</style>
    </div>
  );
}

export default RecommendationsView; 